package ai.wagemix.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initTermsPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("terms.md", FrontMatterElement.Builder { addScalar("title", "Terms of Use"); }))
}

@Page("/terms")
@Layout("ai.wagemix.components.layouts.MarkdownLayout")
@Composable
fun TermsPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("wagemix-terms-of-use") }) {
        org.jetbrains.compose.web.dom.Text("WageMix Terms of Use")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Last Updated:")
        }
        org.jetbrains.compose.web.dom.Text(" January 20, 2026")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("1-acceptance-of-terms") }) {
        org.jetbrains.compose.web.dom.Text("1. Acceptance of Terms")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("By accessing or using WageMix (\"Services\"), you agree to be bound by these Terms of Use and our ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Privacy Policy")
        }
        org.jetbrains.compose.web.dom.Text(", which is incorporated herein by reference. These Terms constitute a legally binding agreement between you (\"User\") and WageMix (\"we,\" \"us,\" or \"our\"). If you do not agree, do not use our Services.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("2-description-of-service") }) {
        org.jetbrains.compose.web.dom.Text("2. Description of Service")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("WageMix provides data analysis and \"mixing\" tools based on U.S. Bureau of Labor Statistics (BLS) Occupational Employment and Wage Statistics (OEWS).")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("AI Functionality:")
            }
            org.jetbrains.compose.web.dom.Text(" We utilize probabilistic algorithms and Large Language Models (LLMs) to parse job descriptions and suggest occupational blends.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Geographic Scope:")
            }
            org.jetbrains.compose.web.dom.Text(" Services are intended ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("exclusively for the United States")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("3-user-accounts-security") }) {
        org.jetbrains.compose.web.dom.Text("3. User Accounts & Security")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("3-1-registration-billing-information") }) {
        org.jetbrains.compose.web.dom.Text("3.1 Registration & Billing Information")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You agree to provide accurate information, including a valid email address and secure password. Failure to maintain accurate account or billing information may result in service interruption.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("3-2-single-user-policy") }) {
        org.jetbrains.compose.web.dom.Text("3.2 Single User Policy")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Each account is for a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("single named user")
        }
        org.jetbrains.compose.web.dom.Text(". Concurrent sessions or \"seat sharing\" are prohibited. WageMix reserves the right to monitor for anomalous login patterns and may suspend accounts found to be in violation.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("4-subscription-and-payment") }) {
        org.jetbrains.compose.web.dom.Text("4. Subscription and Payment")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("4-1-billing-click-to-cancel") }) {
        org.jetbrains.compose.web.dom.Text("4.1 Billing & Click-to-Cancel")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Payments are processed via Stripe. In compliance with the FTC Click-to-Cancel Rule, you may cancel at any time via your dashboard.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("4-2-refunds-non-payment") }) {
        org.jetbrains.compose.web.dom.Text("4.2 Refunds & Non-Payment")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Fees are non-refundable except where mandated by law. Failure to pay fees when due may result in the immediate suspension or termination of access.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("4-3-price-changes") }) {
        org.jetbrains.compose.web.dom.Text("4.3 Price Changes")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We reserve the right to change pricing or tier limits. We will provide at least ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("30 days’ notice")
        }
        org.jetbrains.compose.web.dom.Text(" before any material changes to recurring subscription fees take effect.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("5-acceptable-use-prohibited-activities") }) {
        org.jetbrains.compose.web.dom.Text("5. Acceptable Use & Prohibited Activities")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("5-1-restrictions") }) {
        org.jetbrains.compose.web.dom.Text("5.1 Restrictions")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You agree not to:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Use the Services for ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("antitrust violations")
            }
            org.jetbrains.compose.web.dom.Text(", wage-fixing, or price-fixing.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Upload job descriptions containing ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Personally Identifiable Information (PII)")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Resell, redistribute, or sublicense")
            }
            org.jetbrains.compose.web.dom.Text(" access to WageMix data or Services.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Impersonate others, misrepresent your affiliation, or interfere with the proper functioning of the Services.")
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("5-2-independent-decision-making") }) {
        org.jetbrains.compose.web.dom.Text("5.2 Independent Decision Making")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You warrant that you are using WageMix for independent analysis and not to coordinate wages with competitors.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("6-intellectual-property-license") }) {
        org.jetbrains.compose.web.dom.Text("6. Intellectual Property & License")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("6-1-ownership-license-to-use") }) {
        org.jetbrains.compose.web.dom.Text("6.1 Ownership & License to Use")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We own the WageMix software, proprietary \"mixing\" methodologies, and branding. Subject to these Terms, we grant you a limited, non-exclusive, non-transferable license to access the Services for your ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("internal business purposes")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("6-2-bls-data-specifics") }) {
        org.jetbrains.compose.web.dom.Text("6.2 BLS Data Specifics")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Wage data is sourced from the public domain (BLS). You acknowledge that BLS data reflects ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("historical estimates")
        }
        org.jetbrains.compose.web.dom.Text(", may be ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("suppressed or bounded")
        }
        org.jetbrains.compose.web.dom.Text(", and regional variations may not be fully captured.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("6-3-ai-model-training") }) {
        org.jetbrains.compose.web.dom.Text("6.3 AI & Model Training")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You grant us a license to use ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("anonymized, de-identified metadata")
        }
        org.jetbrains.compose.web.dom.Text(" from your usage to improve our AI. Patterns integrated into our trained model weights cannot be individually deleted.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("7-disclaimers-third-party-services") }) {
        org.jetbrains.compose.web.dom.Text("7. Disclaimers & Third-Party Services")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("7-1-ai-human-in-the-loop") }) {
        org.jetbrains.compose.web.dom.Text("7.1 AI & \"Human-in-the-Loop\"")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("AI suggestions may produce \"hallucinations.\" ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("A Human-in-the-Loop is mandatory.")
        }
        org.jetbrains.compose.web.dom.Text(" You must verify all mixes against official SOC code definitions.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("7-2-no-professional-advice") }) {
        org.jetbrains.compose.web.dom.Text("7.2 No Professional Advice")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("WAGEMIX IS NOT A LAW FIRM.")
        }
        org.jetbrains.compose.web.dom.Text(" Results do not constitute a \"Good Faith Estimate\" for pay transparency laws or determine FLSA \"Exempt\" status.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("7-3-third-party-services") }) {
        org.jetbrains.compose.web.dom.Text("7.3 Third-Party Services")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("WageMix integrates with third-party providers (e.g., Stripe, OpenAI). We are not liable for the uptime or service practices of these third parties.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("8-liability-indemnification") }) {
        org.jetbrains.compose.web.dom.Text("8. Liability & Indemnification")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("8-1-limitation-of-liability") }) {
        org.jetbrains.compose.web.dom.Text("8.1 Limitation of Liability")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("TO THE MAXIMUM EXTENT PERMITTED BY LAW:")
        }
        org.jetbrains.compose.web.dom.Text(" OUR TOTAL LIABILITY SHALL NOT EXCEED THE TOTAL FEES PAID BY YOU IN THE 12 MONTHS PRECEDING A CLAIM.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("8-2-jurisdictional-variations") }) {
        org.jetbrains.compose.web.dom.Text("8.2 Jurisdictional Variations")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In jurisdictions that do not allow certain limitations, our liability is limited to the greatest extent permitted by law.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("8-3-indemnification") }) {
        org.jetbrains.compose.web.dom.Text("8.3 Indemnification")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You agree to indemnify WageMix from any claims, damages, or legal fees arising from your use of the Services, violation of these Terms, or violation of any labor or antitrust laws.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("9-modifications-termination") }) {
        org.jetbrains.compose.web.dom.Text("9. Modifications & Termination")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("9-1-right-to-modify") }) {
        org.jetbrains.compose.web.dom.Text("9.1 Right to Modify")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We reserve the right to modify, suspend, or discontinue any feature or the Service itself at any time.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("9-2-termination-by-wagemix") }) {
        org.jetbrains.compose.web.dom.Text("9.2 Termination by WageMix")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We may terminate your access immediately, without notice, for conduct that violates these Terms or for ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("extended periods of inactivity")
        }
        org.jetbrains.compose.web.dom.Text(" on free accounts. Upon termination, your right to use the Services ceases immediately.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("10-dispute-resolution") }) {
        org.jetbrains.compose.web.dom.Text("10. Dispute Resolution")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("10-1-informal-resolution") }) {
        org.jetbrains.compose.web.dom.Text("10.1 Informal Resolution")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Before filing a claim, you agree to allow at least ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("30 days for informal resolution")
        }
        org.jetbrains.compose.web.dom.Text(" via good-faith negotiation.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("10-2-arbitration-jury-trial-waiver") }) {
        org.jetbrains.compose.web.dom.Text("10.2 Arbitration & Jury Trial Waiver")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If unresolved, disputes shall be resolved via binding arbitration in ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Travis County, Texas")
        }
        org.jetbrains.compose.web.dom.Text(". ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("YOU AND WAGEMIX AGREE TO WAIVE THE RIGHT TO A JURY TRIAL.")
        }
        org.jetbrains.compose.web.dom.Text(" Each party shall bear its own costs and attorney’s fees unless the arbitrator awards otherwise.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("10-3-class-action-waiver") }) {
        org.jetbrains.compose.web.dom.Text("10.3 Class Action Waiver")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Any proceeding shall be conducted only on an ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("individual basis")
        }
        org.jetbrains.compose.web.dom.Text(" and not as a class, collective, or representative action.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("10-4-small-claims-court-exception") }) {
        org.jetbrains.compose.web.dom.Text("10.4 Small Claims Court Exception")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Either party may bring an individual claim in a Texas small claims court if the claim does not exceed the jurisdictional limit of that court.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("11-miscellaneous") }) {
        org.jetbrains.compose.web.dom.Text("11. Miscellaneous")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Entire Agreement:")
            }
            org.jetbrains.compose.web.dom.Text(" These Terms (and the Privacy Policy) constitute the entire agreement.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Severability:")
            }
            org.jetbrains.compose.web.dom.Text(" If any provision is found unenforceable, the remaining provisions remain in effect.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Waiver & Assignment:")
            }
            org.jetbrains.compose.web.dom.Text(" Failure to enforce a provision is not a waiver. We may assign these Terms without restriction; you may not.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Force Majeure:")
            }
            org.jetbrains.compose.web.dom.Text(" We are not liable for delays caused by events beyond our control (e.g., AWS outages, government data feed shutdowns).")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("12-contact-information") }) {
        org.jetbrains.compose.web.dom.Text("12. Contact Information")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Email:")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        com.varabyte.kobweb.silk.components.navigation.Link("mailto:support@wagemix.ai") {
            org.jetbrains.compose.web.dom.Text("support@wagemix.ai")
        }
        org.jetbrains.compose.web.dom.Br()
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Company:")
        }
        org.jetbrains.compose.web.dom.Text(" WageMix")
        org.jetbrains.compose.web.dom.Br()
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Address:")
        }
        org.jetbrains.compose.web.dom.Text(" 501 17th Street S.W., Jamestown, ND 58401-6215")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("13-acknowledgment") }) {
        org.jetbrains.compose.web.dom.Text("13. Acknowledgment")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("By using WageMix, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.")
    }
}
